import { RuntimeRouteEntry } from "./runtimeRouteConfig";

// Identity is path AND kind, not path alone: an override that changes only the kind of a
// path is a different claim on that address, so it is added alongside the base entry and
// left for applyRuntimeRoutes to resolve, rather than silently replacing it.
function sameClaim(a: RuntimeRouteEntry, b: RuntimeRouteEntry): boolean {
  return a.path === b.path && a.kind === b.kind;
}

/**
 * Layers per-browser override entries onto a base declaration. An override that shares a
 * base entry's (path, kind) replaces it where it sits — so the combined list keeps the base
 * order, and applyRuntimeRoutes sees the same collision/ordering it would for the base alone
 * on every address the developer did not touch. Any override that matches nothing is appended.
 *
 * Both lists are already validated by parseRuntimeRouteConfig, so subtree paths arrive
 * trailing-slash-normalised and the match compares the same normalised form on both sides.
 *
 * @returns A new combined list; the inputs are not mutated.
 */
export function overlayRuntimeRoutes(base: RuntimeRouteEntry[], overrides: RuntimeRouteEntry[]): RuntimeRouteEntry[] {
  const combined = base.map((entry) => overrides.find((override) => sameClaim(override, entry)) ?? entry);
  const appended = overrides.filter((override) => !base.some((entry) => sameClaim(override, entry)));
  return [...combined, ...appended];
}
